import SendRounded from '@mui/icons-material/SendRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { dateTime } from '../../lib/format';
import { errorMessage } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { IconTile } from '../shop/ui';
import {
  fetchOlder,
  useMarkSupportRead,
  useSendSupportMessage,
  useSupportChat,
  type SupportMessage,
} from './api';

const MAX_MESSAGE = 4000;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[+\d][\d\s()-]{5,39}$/;

function Bubble({ message, mine }: { message: SupportMessage; mine: boolean }) {
  const { format } = useShop();
  const contact = [message.contactEmail, message.contactPhone].filter(Boolean).join(' · ');
  return (
    <Box
      sx={{
        alignSelf: mine ? 'flex-end' : 'flex-start',
        maxWidth: { xs: '88%', sm: '75%' },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.25, textAlign: mine ? 'right' : 'left' }}
      >
        {message.author.name} · {dateTime(message.createdAt, format)}
      </Typography>
      <Box
        sx={(t) => ({
          px: 1.75,
          py: 1.25,
          borderRadius: 3,
          borderTopRightRadius: mine ? 6 : undefined,
          borderTopLeftRadius: mine ? undefined : 6,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          bgcolor: mine ? 'primary.main' : 'background.paper',
          color: mine ? 'primary.contrastText' : 'text.primary',
          border: mine ? 'none' : `1px solid ${(t.vars ?? t).palette.divider}`,
        })}
      >
        {message.body}
      </Box>
      {mine && contact && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.25, textAlign: 'right' }}
        >
          Reach me at {contact}
        </Typography>
      )}
    </Box>
  );
}

/**
 * The shop's chat with PackBytes support: the thread (polled for replies) and a composer with
 * optional contact email and phone. Used on the Support page and on the locked screen.
 */
export function SupportChat({
  height = 460,
  draft = '',
}: {
  height?: number | string;
  /** Text to start the composer with (e.g. from the Subscription page). */
  draft?: string;
}) {
  const { me } = useAuth();
  const chat = useSupportChat();
  const send = useSendSupportMessage();
  const markRead = useMarkSupportRead();
  const [older, setOlder] = useState<SupportMessage[]>([]);
  const [olderMore, setOlderMore] = useState<boolean | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [body, setBody] = useState(draft);
  const [email, setEmail] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [showContact, setShowContact] = useState<boolean | null>(null);
  const logRef = useRef<HTMLDivElement>(null);

  const conversation = chat.data?.conversation ?? null;
  const messages = useMemo(
    () => [...older, ...(chat.data?.messages ?? [])],
    [older, chat.data?.messages],
  );
  const hasMore = olderMore ?? chat.data?.hasMore ?? false;
  // Contact fields start from the last details given, else the user's own profile.
  const emailValue = email ?? conversation?.contactEmail ?? me?.user.email ?? '';
  const phoneValue = phone ?? conversation?.contactPhone ?? me?.user.phone ?? '';
  const contactOpen = showContact ?? !conversation;
  const emailError = emailValue.trim() !== '' && !EMAIL.test(emailValue.trim());
  const phoneError = phoneValue.trim() !== '' && !PHONE.test(phoneValue.trim());

  const unread = conversation?.unread ?? 0;
  const { mutate: markAsRead } = markRead;
  useEffect(() => {
    if (unread > 0) markAsRead();
  }, [unread, markAsRead]);

  const lastId = messages.at(-1)?.id;
  // Keep the newest message in view (scrolls the thread only, never the page).
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [lastId]);

  const canSend =
    body.trim().length > 0 && body.length <= MAX_MESSAGE && !emailError && !phoneError;
  const submit = () => {
    if (!canSend || send.isPending) return;
    send.mutate(
      {
        body: body.trim(),
        contactEmail: emailValue.trim() || undefined,
        contactPhone: phoneValue.trim() || undefined,
      },
      {
        onSuccess: () => {
          setBody('');
          setShowContact(false);
        },
      },
    );
  };

  const loadOlder = async () => {
    const first = messages[0];
    if (!first) return;
    setLoadingOlder(true);
    try {
      const page = await fetchOlder(first.createdAt);
      setOlder((prev) => [...page.messages, ...prev]);
      setOlderMore(page.hasMore);
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <Stack sx={{ height, minHeight: 0 }}>
      <Box
        ref={logRef}
        role="log"
        aria-label="Messages with PackBytes support"
        aria-live="polite"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: { xs: 1.5, sm: 2 },
          bgcolor: 'action.hover',
          borderRadius: 2,
        }}
      >
        {chat.isPending ? (
          <Box sx={{ m: 'auto' }}>
            <CircularProgress size={24} />
          </Box>
        ) : chat.isError ? (
          <Alert severity="error">{errorMessage(chat.error)}</Alert>
        ) : messages.length === 0 ? (
          <Stack spacing={1.5} sx={{ m: 'auto', alignItems: 'center', textAlign: 'center', px: 2 }}>
            <IconTile size={52}>
              <SupportAgentRounded />
            </IconTile>
            <Typography variant="h6">Message PackBytes support</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
              Ask about your subscription, renewing, or how to use PackPilot. Our replies appear
              right here.
            </Typography>
          </Stack>
        ) : (
          <>
            {hasMore && (
              <Button
                size="small"
                onClick={() => void loadOlder()}
                disabled={loadingOlder}
                sx={{ alignSelf: 'center' }}
              >
                {loadingOlder ? 'Loading…' : 'Show earlier messages'}
              </Button>
            )}
            {messages.map((m) => (
              <Bubble key={m.id} message={m} mine={m.sender === 'SHOP'} />
            ))}
            {conversation?.status === 'RESOLVED' && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ alignSelf: 'center', textAlign: 'center' }}
              >
                Marked as resolved by support. Send a message to reopen it.
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        sx={{ pt: 1.5 }}
      >
        <Collapse in={contactOpen}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField
              label="Your email (optional)"
              type="email"
              size="small"
              fullWidth
              value={emailValue}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
              helperText={emailError ? 'Enter a valid email address.' : undefined}
              slotProps={{ htmlInput: { maxLength: 254 } }}
            />
            <TextField
              label="Phone or WhatsApp (optional)"
              type="tel"
              size="small"
              fullWidth
              value={phoneValue}
              onChange={(e) => setPhone(e.target.value)}
              error={phoneError}
              helperText={phoneError ? 'Enter a valid phone number.' : undefined}
              slotProps={{ htmlInput: { maxLength: 40 } }}
            />
          </Stack>
        </Collapse>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
          <TextField
            label="Message"
            placeholder="Type your message…"
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                submit();
              }
            }}
            error={body.length > MAX_MESSAGE}
            helperText={
              body.length > MAX_MESSAGE
                ? `At most ${String(MAX_MESSAGE)} characters.`
                : 'Ctrl + Enter to send'
            }
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!canSend || send.isPending}
            endIcon={
              send.isPending ? <CircularProgress size={16} color="inherit" /> : <SendRounded />
            }
            sx={{ mb: 3, flexShrink: 0 }}
          >
            Send
          </Button>
        </Stack>
        {send.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMessage(send.error)}
          </Alert>
        )}
        {!contactOpen && (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setShowContact(true)}
          >
            {emailValue || phoneValue
              ? `We can also reach you at ${[emailValue, phoneValue].filter(Boolean).join(' · ')} — change`
              : 'Add your email or phone (optional)'}
          </Link>
        )}
      </Box>
    </Stack>
  );
}
